/**
 * penaltyShootout — deriving the shootout from the event log.
 *
 * Client ask: "AGregar para lo de los penales, se debe colocar que los
 * jugadores fallan o hacen gol." The cockpit could already REACH the
 * `PENALTIES` phase, but a shootout kick had no representation at all — the
 * `Penalty` quick action is the in-play foul-award flow (PMA-COCK-038,
 * `FoulCommitted` + `SetPiece/Penalty` for the fouled player), which is a
 * different thing entirely.
 *
 * ## Why a flagged SetPiece and not a new event type
 *
 * A shootout kick is stored as the SetPiece the taxonomy already has
 * (`set_piece_type: "Penalty"`) carrying `data.is_shootout: true`. That keeps
 * every existing path — the backend enum, the WS duplicate rules, the review
 * editor's outcome dropdown, the ingestion pipeline — working untouched, and
 * `is_shootout` is the single question every consumer asks to tell a shootout
 * kick apart from a penalty awarded during play.
 *
 * Consumers that MUST check it:
 *  - `usePlayerStats` / the backend player aggregates: a shootout goal is not
 *    a goal in the player's tally and never generates an assist.
 *  - Anything counting penalties won or conceded during play.
 *
 * The scoreline needs no guard: `useLiveScore` counts `Shot` events with
 * `outcome: "Goal"` only, so a shootout kick can never move it. That is the
 * correct football result too — a shootout does not change the score, it
 * decides the tie beside it.
 */
import { formatDisplayName } from "../../../lib/nameFormat";
import type { MatchEvent } from "../../../store/useMatchLogStore";
import type { Match } from "../types";

export const SHOOTOUT_OUTCOMES = ["Goal", "Saved", "Missed"] as const;
export type ShootoutOutcome = (typeof SHOOTOUT_OUTCOMES)[number];

export type ShootoutSide = "home" | "away";

export interface ShootoutKick {
  /** Stable identity for React keys and undo — `_id`, else `client_id`. */
  key: string;
  side: ShootoutSide;
  teamId: string;
  playerId: string | null;
  outcome: ShootoutOutcome;
  /** 1-based position in the whole shootout, both teams counted. */
  order: number;
  /** True until the server has acknowledged it. */
  pending: boolean;
}

export interface ShootoutTeamState {
  taken: number;
  scored: number;
  /** In kicking order, for the dot strip. */
  results: ShootoutOutcome[];
}

export interface ShootoutState {
  kicks: ShootoutKick[];
  home: ShootoutTeamState;
  away: ShootoutTeamState;
  /** Suggested next kicker's side — strict alternation off the last kick. */
  nextSide: ShootoutSide;
  /** 1-based number of the kick about to be taken by `nextSide`. */
  nextRound: number;
}

const normalizeOutcome = (raw: unknown): ShootoutOutcome | null => {
  const value = String(raw ?? "").toLowerCase();
  if (value === "goal") return "Goal";
  if (value === "saved") return "Saved";
  if (value === "missed") return "Missed";
  return null;
};

/**
 * True for a penalty taken in the shootout, false for one awarded during
 * play. The flag is the only signal — a period check would be wrong, since a
 * penalty can be awarded in extra time and the shootout carries the same
 * period number as the phase it runs in.
 */
export const isShootoutKick = (event: MatchEvent): boolean =>
  event.type === "SetPiece" &&
  String(event.data?.set_piece_type ?? "").toLowerCase() === "penalty" &&
  event.data?.is_shootout === true;

const emptyTeam = (): ShootoutTeamState => ({
  taken: 0,
  scored: 0,
  results: [],
});

/**
 * Stable per-event identity for de-duplicating kicks, mirroring
 * `useLogIntegrityChecks.ts`'s own `eventIdentity`/`dedupeByStableIdentity`
 * (same codebase, same hazard, independently documented there first).
 * `client_id` is preferred over `_id` deliberately, not incidentally:
 * `useMatchSocket.ts`'s `sendEvent()` assigns `client_id` once and it never
 * changes for the event's entire lifecycle, while `_id` only appears once an
 * ack lands. A kick can be present in the store's `liveEvents` array as the
 * post-ack copy (has both `_id` and `client_id`) at the same time a STALE
 * pre-ack reference to the identical kick — sharing the same `client_id`,
 * missing `_id` — lingers in `queuedEvents` (see `computeShootout`'s own doc
 * comment below). Computing identity `_id`-first, like `ShootoutKick.key`
 * does for React's sake, would give those two copies of one real kick two
 * DIFFERENT identities and defeat the dedup entirely — this must stay
 * `client_id`-first. Returns null (no dedup) for a kick carrying neither id,
 * matching the same "only dedupe a genuinely stable identity" rule.
 */
const shootoutEventIdentity = (event: MatchEvent): string | null =>
  event.client_id ?? event._id ?? null;

/**
 * Fold the event log into the shootout's current standing.
 *
 * Events are read in the order given; the caller passes `liveEvents`, which
 * the store already keeps in chronological order (see `lib/eventTime.ts` for
 * why that ordering is not a plain `new Date` sort).
 *
 * De-dupes by `shootoutEventIdentity` before folding (EPIC-34 criterion 6).
 * `useCockpitShootout.ts` calls this with `[...liveEvents, ...queuedEvents]`,
 * and `useMatchLogStore`'s two arrays legitimately hold the SAME kick event
 * at once whenever it is optimistically live and not yet synced --
 * `sendEvent()`'s offline branch does this by construction (`addLiveEvent`
 * unconditionally, `addQueuedEvent` too when offline), and `expireAck`
 * (ack-timeout requeue) / `flushPendingAcksToQueue` (fires on every WS
 * reconnect) reach the identical dual-membership by adding the event to
 * `queuedEvents` without ever removing it from `liveEvents` first. That is a
 * known, accepted shape of the store elsewhere (`useLogIntegrityChecks.ts`
 * already guards its own fold against it) -- the fix here is the same
 * guard, not a change to the store. Without it, one real kick folds into two
 * `ShootoutKick` entries sharing an identical `key`, which is the "two
 * children with the same key" React warning `e2e/penalty-shootout.spec.ts`
 * caught paired with EPIC-35's long, load-bearing scenarios (an ack timeout
 * or a reconnect during a multi-minute scripted match is far more reachable
 * than in a short isolated shootout spec run).
 */
export function computeShootout(
  match: Pick<Match, "home_team" | "away_team"> | null,
  events: MatchEvent[],
): ShootoutState {
  const home = emptyTeam();
  const away = emptyTeam();
  const kicks: ShootoutKick[] = [];

  if (!match) {
    return { kicks, home, away, nextSide: "home", nextRound: 1 };
  }

  const homeIds = new Set(
    [match.home_team.id, match.home_team.team_id].filter(Boolean) as string[],
  );

  const seenIdentities = new Set<string>();

  for (const event of events) {
    if (!isShootoutKick(event)) continue;

    const identity = shootoutEventIdentity(event);
    if (identity !== null) {
      if (seenIdentities.has(identity)) continue;
      seenIdentities.add(identity);
    }

    const outcome = normalizeOutcome(event.data?.outcome);
    // An unrecognised outcome is a kick we cannot score, so it is left out
    // of the tally entirely rather than silently counted as a miss.
    if (!outcome) continue;

    const side: ShootoutSide = homeIds.has(event.team_id ?? "")
      ? "home"
      : "away";
    const team = side === "home" ? home : away;
    team.taken += 1;
    team.results.push(outcome);
    if (outcome === "Goal") team.scored += 1;

    kicks.push({
      key: event._id || event.client_id || `${event.timestamp}-${kicks.length}`,
      side,
      teamId: event.team_id ?? "",
      playerId: event.player_id ?? null,
      outcome,
      order: kicks.length + 1,
      pending: Boolean(event.client_id && !event._id),
    });
  }

  const last = kicks[kicks.length - 1];
  // Strict alternation, which holds through sudden death too. With no kicks
  // yet the home side is offered first; the operator can always override.
  const nextSide: ShootoutSide = last
    ? last.side === "home"
      ? "away"
      : "home"
    : "home";
  const nextRound = (nextSide === "home" ? home.taken : away.taken) + 1;

  return { kicks, home, away, nextSide, nextRound };
}

/**
 * PMA-COCK-164: labeled kick rows for the public timeline's shootout block
 * (`PublicMatchTimeline`'s `shootoutKicks` prop, the single public shootout
 * surface — `PublicShootoutBoard` is no longer mounted on the public page,
 * lead decision 2026-09-07). Resolves each kick's player id to the same
 * `#<jersey> <name>` label `PublicShootoutBoard` itself uses (its component
 * file is untouched for any other consumer/test). Kept beside
 * `computeShootout` rather than inline in `PublicMatchAnalytics.tsx` so the
 * name-resolution rule has one home, not independently-maintained copies.
 */
export interface PublicShootoutKickEntry {
  key: string;
  order: number;
  side: ShootoutSide;
  playerLabel: string;
  outcome: ShootoutOutcome;
}

export function derivePublicShootoutRows(
  match: Pick<Match, "home_team" | "away_team"> | null,
  state: ShootoutState,
): PublicShootoutKickEntry[] {
  if (!match) return [];
  return state.kicks.map((kick) => {
    const team = kick.side === "home" ? match.home_team : match.away_team;
    const player = kick.playerId
      ? team.players.find((p) => p.id === kick.playerId)
      : undefined;
    const playerLabel = player
      ? `#${player.jersey_number} ${formatDisplayName(player.full_name)}`
      : (kick.playerId ?? "");
    return {
      key: kick.key,
      order: kick.order,
      side: kick.side,
      playerLabel,
      outcome: kick.outcome,
    };
  });
}

/**
 * Fixed by the client's own decision (2026-09-16) — a shootout is always
 * best-of-5 with no per-match or per-competition override. Kept as one named
 * constant so `resolveShootoutWinner` and `describeShootoutRound` cannot
 * drift onto two different numbers.
 */
export const SHOOTOUT_BEST_OF = 5;

/**
 * Whether the shootout is mathematically decided — no remaining kick can
 * change the winner.
 *
 * Used only to label the panel; it never disables logging. A referee can end
 * a shootout at a point the naive maths disagrees with, and a mis-logged
 * kick corrected later can un-decide it, so this is a hint and not a gate.
 */
export function resolveShootoutWinner(
  state: ShootoutState,
  bestOf = SHOOTOUT_BEST_OF,
): ShootoutSide | null {
  const { home, away } = state;
  const homeLeft = Math.max(0, bestOf - home.taken);
  const awayLeft = Math.max(0, bestOf - away.taken);

  if (home.scored > away.scored + awayLeft) return "home";
  if (away.scored > home.scored + homeLeft) return "away";

  // Sudden death: both past the first `bestOf`, equal kicks taken, one ahead.
  if (
    home.taken >= bestOf &&
    away.taken >= bestOf &&
    home.taken === away.taken &&
    home.scored !== away.scored
  ) {
    return home.scored > away.scored ? "home" : "away";
  }

  return null;
}

/** Whether the first `bestOf` rounds are still running or sudden death has begun. */
export type ShootoutRoundPhase = "round" | "suddenDeath";

/**
 * PMA-COCK-201: the panel's round/sudden-death/decided labelling, derived
 * fresh from `state` every time — never stored, never latched. `phase` and
 * `winner` are independent axes, not a single enum: a shootout is very often
 * decided before either side has taken all `bestOf` kicks (e.g. 3-0 up with
 * two kicks left for the trailing side), so `winner` can be non-null while
 * `phase` is still `"round"`. Consumed by both the cockpit panel and the
 * public timeline (`PublicMatchTimeline`'s `shootoutRound` prop) so the two
 * surfaces read the same round/sudden-death state off the same function
 * rather than maintaining their own copies that can drift apart.
 */
export interface ShootoutRoundDescriptor {
  phase: ShootoutRoundPhase;
  /** 1-based number of the kick `state.nextSide` is about to take. */
  round: number;
  /**
   * The side `resolveShootoutWinner` currently names, or null while
   * undecided. A HINT for the panel's label, never a gate: logging a further
   * kick must remain possible after this turns non-null, and correcting a
   * mis-logged kick that un-decides the shootout must turn it back to null
   * on the very next render — see `resolveShootoutWinner`'s own doc comment.
   */
  winner: ShootoutSide | null;
}

export function describeShootoutRound(
  state: ShootoutState,
  bestOf = SHOOTOUT_BEST_OF,
): ShootoutRoundDescriptor {
  // A kick is a sudden-death kick for the side about to take it once THAT
  // side's own count has exhausted its first `bestOf` allotment —
  // `state.nextRound` already is exactly "that side's taken count + 1"
  // (see `computeShootout`). The old predicate required BOTH sides to have
  // taken >= `bestOf`, which only ever holds under perfect strict
  // alternation. `PenaltyShootoutPanel.tsx`'s side-picker override
  // (`chooseSide`) lets an operator log a kick for either side regardless
  // of `state.nextSide` — a referee's real order is the authority, not the
  // alternation — so one side's count can outrun the other's. When that
  // happens, `nextRound` for the ahead side climbs past `bestOf` while the
  // old predicate still saw the trailing side under `bestOf` and stayed in
  // the `"round"` phase, producing a self-contradictory "Ronda 6 de 5"
  // label. Bounding on `nextRound` itself instead is a strict
  // generalization: under perfect alternation `nextSide` always holds the
  // smaller-or-equal count, so `nextRound > bestOf` is provably equivalent
  // to the old `home.taken >= bestOf && away.taken >= bestOf` there — this
  // changes behavior only for the unbalanced-count case.
  const suddenDeath = state.nextRound > bestOf;
  return {
    phase: suddenDeath ? "suddenDeath" : "round",
    round: state.nextRound,
    winner: resolveShootoutWinner(state, bestOf),
  };
}
